import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  fetchCurriculumCatalog,
  type CurriculumMatrixCatalog,
} from '@interactive-room';

import type { CourseProfile } from '../../types/courseProfile';
import type {
  RubricCurriculumMode,
  RubricDraft,
  RubricEvaluationType,
  RubricOaSelection,
  RubricProjectProgress,
} from '../../types/rubricDraft';
import {
  RUBRIC_CURRICULUM_MODE_OPTIONS,
  RUBRIC_EVALUATION_TYPE_OPTIONS,
  isCrossSubjectCurriculumMode,
  resolveRubricOas,
  resolveRubricSubjectTags,
  resolveRubricTargetLevels,
  rubricRequiresCurriculumOa,
} from '../../types/rubricDraft';
import {
  curriculumUnavailableMessage,
  hasCurriculumSubjectSupport,
} from '../../constants/subjectsWithCurriculum';
import {
  SUBJECT_CATALOG,
  getCurriculumSubjectOptions,
  getSubjectNameByTag,
} from '../../constants/subjectCatalog';
import { EXAM_LEVEL_OPTIONS } from '../../constants/examAiParams';
import { resolveCourseSubjectTags } from '../../constants/courseProfileUi';
import { RubricOaPicker, type RubricOaCatalogSource } from './RubricOaPicker';

interface RubricConfigPanelProps {
  draft: RubricDraft;
  course: CourseProfile | null;
  isGenerating: boolean;
  onChange: (draft: RubricDraft) => void;
  onGenerate: () => void;
}

function subjectLabelForTag(tag: string): string {
  return getSubjectNameByTag(tag) || SUBJECT_CATALOG.find((subject) => subject.tag === tag)?.name || tag;
}

function applyOasToDraft(draft: RubricDraft, nextOas: RubricOaSelection[]): RubricDraft {
  const primary = nextOas[0];

  return {
    ...draft,
    oas: nextOas,
    oaCode: primary?.code,
    oaText: primary?.text,
    curriculumTarget: primary
      ? {
          axis: primary.axis || draft.curriculumTarget?.axis || '',
          oaCode: primary.code,
          oahCode: draft.curriculumTarget?.oahCode,
          oaaCode: draft.curriculumTarget?.oaaCode,
        }
      : undefined,
  };
}

function oaIdentity(oa: RubricOaSelection): string {
  return `${oa.subjectTag || ''}::${oa.code}`;
}

const PROJECT_PROGRESS_OPTIONS: Array<{
  id: RubricProjectProgress;
  label: string;
  hint: string;
}> = [
  { id: 'inicio', label: 'Inicio', hint: 'Desde cero' },
  { id: 'en_curso', label: 'En curso', hint: 'Con avance' },
  { id: 'casi_terminado', label: 'Cierre', hint: 'Evaluación final' },
];

function FieldLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="rubric-config__label">
      {children}
    </label>
  );
}

export function RubricConfigPanel({
  draft,
  course,
  isGenerating,
  onChange,
  onGenerate,
}: RubricConfigPanelProps) {
  const patch = (partial: Partial<RubricDraft>) => onChange({ ...draft, ...partial });
  const evaluationType = (draft.evaluationType ?? 'formativa') as RubricEvaluationType;
  const curriculumMode = (draft.curriculumMode ?? 'mono') as RubricCurriculumMode;
  const crossSubject = isCrossSubjectCurriculumMode(curriculumMode);
  const courseTag = course?.subjectTag ? String(course.subjectTag).trim().toLowerCase() : '';
  const courseAllTags = useMemo(
    () => (course ? resolveCourseSubjectTags(course) : courseTag ? [courseTag] : []),
    [course, courseTag],
  );
  const courseAdditionalTags = useMemo(
    () => courseAllTags.filter((tag) => tag !== courseTag),
    [courseAllTags, courseTag],
  );

  const subjectTags = useMemo(
    () => resolveRubricSubjectTags(draft, courseTag, courseAdditionalTags),
    [draft, courseTag, courseAdditionalTags],
  );

  const selectableExtraSubjects = useMemo(() => {
    const exclude = new Set(courseAllTags);
    return getCurriculumSubjectOptions()
      .filter((subject) => !exclude.has(subject.tag))
      .map((subject) => ({
        tag: subject.tag,
        label: subject.name,
        hasOaMatrix: hasCurriculumSubjectSupport(subject.tag),
      }));
  }, [courseAllTags]);

  const targetLevels = useMemo(
    () => resolveRubricTargetLevels(draft, course?.level),
    [draft, course?.level],
  );

  const courseHasCurriculum = Boolean(courseTag && hasCurriculumSubjectSupport(courseTag));
  const anySubjectHasCurriculum = subjectTags.some((tag) => hasCurriculumSubjectSupport(tag));
  const showOaPicker = Boolean(course?.level) && anySubjectHasCurriculum;
  const oaRequired = rubricRequiresCurriculumOa(
    draft,
    courseTag,
    hasCurriculumSubjectSupport,
    courseAdditionalTags,
  );

  const [catalogBySubject, setCatalogBySubject] = useState<
    Record<string, CurriculumMatrixCatalog | null>
  >({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    if (!course?.level || !showOaPicker || subjectTags.length === 0) {
      setCatalogBySubject({});
      setCatalogError(null);
      setCatalogLoading(false);
      return;
    }

    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);

    Promise.all(
      subjectTags.map(async (tag) => {
        if (!hasCurriculumSubjectSupport(tag)) {
          return [tag, null] as const;
        }
        try {
          const catalog = await fetchCurriculumCatalog({
            level: course.level,
            subjectTag: tag,
          });
          return [tag, catalog] as const;
        } catch (err) {
          throw err instanceof Error ? err : new Error('No se pudo cargar el catálogo de OA.');
        }
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        const next: Record<string, CurriculumMatrixCatalog | null> = {};
        let found = 0;
        for (const [tag, catalog] of entries) {
          next[tag] = catalog;
          if (catalog) found += 1;
        }
        setCatalogBySubject(next);
        if (found === 0) {
          setCatalogError('No hay OA MINEDUC para el nivel y las asignaturas seleccionadas.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogBySubject({});
          setCatalogError(
            err instanceof Error && err.message
              ? err.message
              : 'No se pudo cargar el catálogo de OA.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [course?.level, showOaPicker, subjectTags.join('|')]);

  const oaSources = useMemo((): RubricOaCatalogSource[] => {
    return subjectTags
      .map((tag) => {
        const catalog = catalogBySubject[tag];
        if (!catalog) return null;
        return {
          subjectTag: tag,
          subjectLabel: subjectLabelForTag(tag),
          catalog,
        };
      })
      .filter((item): item is RubricOaCatalogSource => Boolean(item));
  }, [subjectTags, catalogBySubject]);

  const selectedOas = useMemo(() => resolveRubricOas(draft), [draft]);
  const selectedKeys = useMemo(
    () => new Set(selectedOas.map((oa) => oaIdentity(oa))),
    [selectedOas],
  );

  const toggleOa = (oa: RubricOaSelection) => {
    const key = oaIdentity(oa);
    const exists = selectedKeys.has(key);
    const next = exists
      ? selectedOas.filter((item) => oaIdentity(item) !== key)
      : [
          ...selectedOas,
          {
            code: oa.code,
            text: oa.text,
            axis: oa.axis,
            subjectTag: oa.subjectTag,
            subjectLabel: oa.subjectLabel,
          },
        ];
    onChange(applyOasToDraft(draft, next));
  };

  const handleEvaluationTypeChange = (nextType: RubricEvaluationType) => {
    if (nextType === evaluationType) return;
    if (nextType === 'diagnostica') {
      const seed =
        (draft.targetLevels?.length ? draft.targetLevels : null)
        ?? (course?.level ? [String(course.level)] : []);
      patch({
        evaluationType: nextType,
        targetLevels: seed.map((level) => String(level).trim().toLowerCase()).filter(Boolean),
      });
      return;
    }
    patch({ evaluationType: nextType, targetLevels: [] });
  };

  const toggleTargetLevel = (levelId: string) => {
    const current = new Set(targetLevels);
    if (current.has(levelId)) {
      if (current.size <= 1) return;
      current.delete(levelId);
    } else {
      current.add(levelId);
    }
    // Mantener orden del catálogo EXAM_LEVEL_OPTIONS.
    const ordered = EXAM_LEVEL_OPTIONS.map((option) => option.id).filter((id) => current.has(id));
    patch({ targetLevels: ordered });
  };

  const handleCurriculumModeChange = (mode: RubricCurriculumMode) => {
    if (mode === curriculumMode) return;
    if (mode === 'mono') {
      const courseOas = selectedOas.filter(
        (oa) => !oa.subjectTag || oa.subjectTag === courseTag,
      );
      onChange(
        applyOasToDraft(
          {
            ...draft,
            curriculumMode: 'mono',
            extraSubjectTags: [],
          },
          courseOas,
        ),
      );
      return;
    }
    onChange({
      ...draft,
      curriculumMode: mode,
      extraSubjectTags: draft.extraSubjectTags ?? [],
    });
  };

  const toggleExtraSubject = (tag: string) => {
    const current = new Set(
      (draft.extraSubjectTags ?? []).map((item) => String(item).trim().toLowerCase()),
    );
    if (current.has(tag)) current.delete(tag);
    else current.add(tag);
    const nextExtras = [...current];
    const allowed = new Set([courseTag, ...nextExtras].filter(Boolean));
    const prunedOas = selectedOas.filter(
      (oa) => !oa.subjectTag || allowed.has(oa.subjectTag),
    );
    onChange(
      applyOasToDraft(
        {
          ...draft,
          extraSubjectTags: nextExtras,
        },
        prunedOas,
      ),
    );
  };

  const handleProjectProgressChange = (estadoProyecto: RubricProjectProgress) => {
    if (estadoProyecto === draft.estadoProyecto) return;
    onChange({
      ...draft,
      estadoProyecto,
      avanceDescripcion: estadoProyecto === 'inicio' ? '' : draft.avanceDescripcion ?? '',
    });
  };

  const needsAnotherSubject = crossSubject && subjectTags.length < 2;
  const canGenerate =
    !isGenerating &&
    draft.objective.trim().length >= 8 &&
    !needsAnotherSubject &&
    (evaluationType !== 'diagnostica' || targetLevels.length > 0) &&
    (!oaRequired || selectedOas.length > 0);

  const projectProgress = draft.estadoProyecto ?? 'inicio';
  const showAdvanceField = projectProgress === 'en_curso' || projectProgress === 'casi_terminado';

  return (
    <aside className="rubric-config">
      <div className="rubric-config__scroll">
        <div className="rubric-config__field">
          <FieldLabel htmlFor="rubric-title">Título</FieldLabel>
          <input
            id="rubric-title"
            className="sala-input-dark rubric-config__control w-full"
            value={draft.title}
            onChange={(event) => patch({ title: event.target.value })}
            placeholder="Ej.: Interpretación en flauta dulce"
          />
        </div>

        <div className="rubric-config__field">
          <FieldLabel>Tipo de evaluación</FieldLabel>
          <div className="rubric-config__segment" role="group" aria-label="Tipo de evaluación">
            {RUBRIC_EVALUATION_TYPE_OPTIONS.map((option) => {
              const active = evaluationType === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  title={option.hint}
                  aria-pressed={active}
                  className={`rubric-config__seg${active ? ' rubric-config__seg--active' : ''}`}
                  onClick={() => handleEvaluationTypeChange(option.id)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="rubric-config__footer-hint">
            {evaluationType === 'diagnostica'
              ? 'Diagnóstica: el OA es opcional; puedes generar solo con la intención. Puedes cubrir varios niveles.'
              : RUBRIC_EVALUATION_TYPE_OPTIONS.find((o) => o.id === evaluationType)?.hint}
          </p>
        </div>

        {evaluationType === 'diagnostica' ? (
          <div className="rubric-config__field">
            <FieldLabel>
              Niveles cubiertos
              <span className="rubric-config__required">+1</span>
            </FieldLabel>
            <p className="rubric-config__footer-hint" style={{ marginTop: 0 }}>
              Selecciona uno o más (p. ej. NT1, NT2 y 1° básico) para enmarcar la diagnóstica.
            </p>
            <div className="rubric-config__chips" role="group" aria-label="Niveles curriculares">
              {EXAM_LEVEL_OPTIONS.map((option) => {
                const active = targetLevels.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={active}
                    className={`rubric-config__chip${active ? ' rubric-config__chip--active' : ''}`}
                    onClick={() => toggleTargetLevel(option.id)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="rubric-config__field">
          <FieldLabel>Alcance curricular</FieldLabel>
          <div className="rubric-config__segment" role="group" aria-label="Alcance curricular">
            {RUBRIC_CURRICULUM_MODE_OPTIONS.map((option) => {
              const active = curriculumMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  title={option.hint}
                  aria-pressed={active}
                  className={`rubric-config__seg${active ? ' rubric-config__seg--active' : ''}`}
                  onClick={() => handleCurriculumModeChange(option.id)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="rubric-config__footer-hint">
            {RUBRIC_CURRICULUM_MODE_OPTIONS.find((o) => o.id === curriculumMode)?.hint}
          </p>
        </div>

        {crossSubject ? (
          <div className="rubric-config__field">
            <FieldLabel>
              Asignaturas
              <span className="rubric-config__required">+1</span>
            </FieldLabel>
            <p className="rubric-config__footer-hint" style={{ marginTop: 0 }}>
              Curso:{' '}
              <strong>
                {courseAllTags.map((tag) => subjectLabelForTag(tag)).filter(Boolean).join(' · ') ||
                  '—'}
              </strong>
              . Añade otras del currículo si hace falta. Los OA aparecen solo en asignaturas con
              matriz MINEDUC (matemática, música, lenguaje).
            </p>
            <div className="rubric-config__chips" role="group" aria-label="Asignaturas adicionales">
              {selectableExtraSubjects.map((subject) => {
                const active = (draft.extraSubjectTags ?? []).includes(subject.tag);
                return (
                  <button
                    key={subject.tag}
                    type="button"
                    aria-pressed={active}
                    title={
                      subject.hasOaMatrix
                        ? subject.label
                        : `${subject.label} (sin OA MINEDUC aún; se usa como marco de la evaluación)`
                    }
                    className={`rubric-config__chip${active ? ' rubric-config__chip--active' : ''}${
                      !subject.hasOaMatrix ? ' rubric-config__chip--no-oa' : ''
                    }`}
                    onClick={() => toggleExtraSubject(subject.tag)}
                  >
                    {subject.label}
                    {!subject.hasOaMatrix ? (
                      <span className="rubric-config__chip-note">sin OA</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {needsAnotherSubject ? (
              <p className="rubric-config__footer-hint">
                Selecciona al menos una asignatura adicional.
              </p>
            ) : null}
          </div>
        ) : null}

        {!courseHasCurriculum && courseTag && !crossSubject ? (
          <p className="rubric-config__notice" role="status">
            {curriculumUnavailableMessage(courseTag)}. La rúbrica se genera sin marco OA oficial.
          </p>
        ) : null}

        {showOaPicker && !needsAnotherSubject ? (
          <div className="rubric-config__field">
            <FieldLabel>
              Objetivos de aprendizaje
              {oaRequired ? (
                <span className="rubric-config__required">Requerido</span>
              ) : (
                <span className="rubric-config__required rubric-config__required--optional">
                  Opcional
                </span>
              )}
            </FieldLabel>
            <RubricOaPicker
              sources={oaSources}
              catalogLoading={catalogLoading}
              catalogError={catalogError}
              selectedOas={selectedOas}
              onToggle={toggleOa}
            />
          </div>
        ) : null}

        <div className="rubric-config__field">
          <FieldLabel htmlFor="rubric-objective">Intención evaluativa</FieldLabel>
          <textarea
            id="rubric-objective"
            className="sala-input-dark rubric-config__control min-h-[7.5rem] w-full resize-y"
            value={draft.objective}
            onChange={(event) => patch({ objective: event.target.value })}
            placeholder={
              evaluationType === 'diagnostica'
                ? 'Ej.: detectar qué saben del pulso y la figura negra antes de empezar…'
                : crossSubject
                  ? 'Ej.: evaluar la integración de música y lenguaje en el podcast…'
                  : showOaPicker
                    ? 'Ej.: evaluar solo el canto en la presentación, aunque el OA también mencione tocar…'
                    : 'Describe qué quieres evaluar y el propósito formativo…'
            }
          />
          <p className="rubric-config__footer-hint">
            Brief interno para la IA. No se imprime tal cual: la IA redacta un objetivo de evaluación.
          </p>
        </div>

        {draft.hasMatrix || (draft.evaluationObjective ?? '').trim() ? (
          <div className="rubric-config__field">
            <FieldLabel htmlFor="rubric-eval-objective">Objetivo de la evaluación</FieldLabel>
            <textarea
              id="rubric-eval-objective"
              className="sala-input-dark rubric-config__control min-h-[4.5rem] w-full resize-y"
              value={draft.evaluationObjective ?? ''}
              onChange={(event) => patch({ evaluationObjective: event.target.value })}
              placeholder="Ej.: Identificar el reconocimiento del pulso y de la figura negra en patrones cortos."
            />
            <p className="rubric-config__footer-hint">
              Se imprime en el PDF. Empieza con verbo en infinitivo; tono distante e impersonal (sin dirigirse a la persona).
            </p>
          </div>
        ) : null}

        <div className="rubric-config__field">
          <FieldLabel>Estado del proyecto</FieldLabel>
          <div className="rubric-config__segment" role="group" aria-label="Estado del proyecto">
            {PROJECT_PROGRESS_OPTIONS.map((option) => {
              const active = projectProgress === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  title={option.hint}
                  aria-pressed={active}
                  className={`rubric-config__seg${active ? ' rubric-config__seg--active' : ''}`}
                  onClick={() => handleProjectProgressChange(option.id)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {showAdvanceField ? (
            <div className="rubric-config__field rubric-config__field--nested">
              <FieldLabel htmlFor="rubric-avance">Avance hasta ahora</FieldLabel>
              <textarea
                id="rubric-avance"
                className="sala-input-dark rubric-config__control min-h-[5rem] w-full resize-y"
                value={draft.avanceDescripcion ?? ''}
                onChange={(event) => patch({ avanceDescripcion: event.target.value })}
                placeholder="Ej.: Ya grabamos la mesa de sonido. Falta la locución final."
              />
            </div>
          ) : null}
        </div>
      </div>

      <div className="rubric-config__cta">
        <button
          type="button"
          className="sala-btn-hero w-full shadow-sm"
          onClick={onGenerate}
          disabled={!canGenerate}
        >
          <Sparkles className="mr-2 inline h-4 w-4" strokeWidth={2.25} />
          {isGenerating ? 'Generando matriz…' : 'Generar matriz con IA'}
        </button>
      </div>
    </aside>
  );
}
